import React, { useEffect, useMemo, useState } from 'react';
import { useUserData } from '../../hooks/useUserData';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const DrawdownPriority = () => {
  const { userData, saveDrawdownPriority, loading } = useUserData();
  const { sidebarExpanded, toggleSidebar } = useSidebar();

  // Build unique asset classes by liquidity from current portfolio
  const { liquidTypes, illiquidTypes } = useMemo(() => {
    const portfolio = userData?.portfolio || [];
    const liquid = new Set();
    const illiquid = new Set();
    // Always include Cash as liquid by default
    liquid.add('Cash');
    portfolio.forEach(a => {
      const type = a?.type;
      if (!type) return;
      const isLiquid = String(a?.liquidity || '').toLowerCase() === 'liquid';
      if (isLiquid) liquid.add(type); else illiquid.add(type);
    });
    return { liquidTypes: Array.from(liquid), illiquidTypes: Array.from(illiquid) };
  }, [userData]);

  // Default recommended ordering map (lower index = higher priority)
  const defaultRank = useMemo(() => {
    const order = [
      'Cash',
      'Fixed Deposits',
      'Debt MF',
      'Mutual Funds',
      'Equity',
      'Gold',
      'Rental',
      'Real Estate',
      'Residence'
    ];
    const rank = new Map();
    order.forEach((name, idx) => rank.set(name, idx));
    return rank;
  }, []);

  const sortByDefault = (list) => {
    return [...list].sort((a, b) => {
      const ra = defaultRank.has(a) ? defaultRank.get(a) : 999;
      const rb = defaultRank.has(b) ? defaultRank.get(b) : 999;
      if (ra !== rb) return ra - rb;
      return a.localeCompare(b);
    });
  };

  // Initialize from saved or defaults
  const initial = useMemo(() => {
    const saved = userData?.drawdownPriority;
    const allowIlliquid = Boolean(saved?.allowIlliquid);
    const baseLiquid = Array.isArray(saved?.liquidOrder) && saved.liquidOrder.length > 0
      ? saved.liquidOrder.filter(t => liquidTypes.includes(t))
      : sortByDefault(liquidTypes);
    // Ensure 'Cash' exists and is first
    const liquidOrder = (() => {
      const set = new Set(baseLiquid);
      set.add('Cash');
      const merged = Array.from(set);
      const withoutCash = merged.filter(n => n !== 'Cash');
      return ['Cash', ...withoutCash];
    })();
    const illiquidOrder = Array.isArray(saved?.illiquidOrder) && saved.illiquidOrder.length > 0
      ? saved.illiquidOrder.filter(t => illiquidTypes.includes(t))
      : sortByDefault(illiquidTypes);
    return { allowIlliquid, liquidOrder, illiquidOrder };
  }, [userData, liquidTypes, illiquidTypes]);

  const [allowIlliquid, setAllowIlliquid] = useState(initial.allowIlliquid);
  const [liquidOrder, setLiquidOrder] = useState(initial.liquidOrder);
  const [illiquidOrder, setIlliquidOrder] = useState(initial.illiquidOrder);

  useEffect(() => {
    setAllowIlliquid(initial.allowIlliquid);
    setLiquidOrder(initial.liquidOrder);
    setIlliquidOrder(initial.illiquidOrder);
  }, [initial.allowIlliquid, initial.liquidOrder, initial.illiquidOrder]);

  const isValid = useMemo(() => liquidOrder.length > 0, [liquidOrder]);

  // Drag & drop handlers
  const [dragIdx, setDragIdx] = useState(null);
  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (idx, list, setList) => {
    setList(prev => {
      const moved = prev[dragIdx];
      const copy = prev.filter((_, i) => i !== dragIdx);
      copy.splice(idx, 0, moved);
      return copy;
    });
    setDragIdx(null);
  };

  const resetDefault = () => {
    setLiquidOrder(sortByDefault(liquidTypes));
    setIlliquidOrder(sortByDefault(illiquidTypes));
    setAllowIlliquid(false);
  };

  const savePriority = async () => {
    await saveDrawdownPriority({ allowIlliquid, liquidOrder, illiquidOrder });
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <Header 
        sidebarExpanded={sidebarExpanded}
        onSidebarToggle={toggleSidebar}
      />

      <main
        className={`
          transition-all duration-300 ease-spring pt-16 pb-20 lg:pb-8
          ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}
        `}
      >
        <div className="p-4 sm:p-6 py-6 sm:py-8">
          <Breadcrumb />

          <div className="flex items-center justify-between mb-6 sm:mb-8 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Shuffle" size={16} className="text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-foreground">Drawdown Priority</h1>
                <p className="text-sm text-muted-foreground">Deficit waterfall sells from top to bottom until covered.</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <Button variant="outline" size="sm" onClick={resetDefault}>
                <Icon name="RotateCcw" size={16} />
                Reset to Default
              </Button>
              <Button onClick={savePriority} disabled={!isValid || loading}>
                <Icon name="Save" size={16} />
                Save Priority
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Liquid assets ordering */}
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="Droplets" size={16} className="text-success" />
                  </div>
                  <h3 className="font-medium text-foreground">Liquid Assets Order</h3>
                </div>
                <span className="text-xs text-muted-foreground">Drag to reorder</span>
              </div>
              <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                {liquidOrder.map((name, idx) => (
                  <li
                    key={`${name}-${idx}`}
                    className="flex items-center justify-between px-3 py-2 bg-card/50 hover:bg-muted/30 cursor-move"
                    draggable
                    onDragStart={() => onDragStart(idx)}
                    onDragOver={onDragOver}
                    onDrop={() => onDrop(idx, liquidOrder, setLiquidOrder)}
                  >
                    <div className="flex items-center gap-2">
                      <Icon name="GripVertical" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">{name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">#{idx + 1}</span>
                  </li>
                ))}
                {liquidOrder.length === 0 && (
                  <li className="px-3 py-3 text-sm text-muted-foreground">No liquid asset classes found</li>
                )}
              </ul>
            </div>

            {/* Illiquid assets ordering (optional) */}
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
                    <Icon name="Anchor" size={16} className="text-warning" />
                  </div>
                  <h3 className="font-medium text-foreground">Illiquid Assets Order</h3>
                </div>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" className="w-4 h-4" checked={allowIlliquid} onChange={(e) => setAllowIlliquid(e.target.checked)} />
                  <span className="text-foreground">Allow selling if liquid is exhausted</span>
                </label>
              </div>

              {allowIlliquid ? (
                <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                  {illiquidOrder.map((name, idx) => (
                    <li
                      key={`${name}-${idx}`}
                      className="flex items-center justify-between px-3 py-2 bg-card/50 hover:bg-muted/30 cursor-move"
                      draggable
                      onDragStart={() => onDragStart(idx)}
                      onDragOver={onDragOver}
                      onDrop={() => onDrop(idx, illiquidOrder, setIlliquidOrder)}
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="GripVertical" size={16} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">{name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">#{idx + 1}</span>
                    </li>
                  ))}
                  {illiquidOrder.length === 0 && (
                    <li className="px-3 py-3 text-sm text-muted-foreground">No illiquid asset classes found</li>
                  )}
                </ul>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Illiquid assets will be ignored unless enabled.
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DrawdownPriority;


